import React, { useState } from "react";
import axios from "axios";
import { Check } from "lucide-react";
import { useAttribution } from "../context/AttributionContext";
import ObfuscatedEmail from "./ObfuscatedEmail";
import {
    PROPERTY_STATUS_OPTIONS,
    PROJECT_TYPE_OPTIONS,
    BUDGET_OPTIONS,
    TIMING_OPTIONS,
    UK_POSTCODE_RE,
} from "../lib/enums";


// COPY LOCKED — labels and CTAs on this form are client-supplied.


const API = "https://studio-g4.co.uk/api";


const INITIAL = {
    postcode: "",
    property_status: "",
    project_types: [],
    project_description: "",
    construction_budget: "",
    construction_timing: "",
    full_name: "",
    email: "",
    phone: "",
    website: "",
};


function Kicker() {
    return (
        <>
            <p className="font-manrope font-semibold text-terracotta text-[12px] tracking-[0.22em] uppercase mb-4">Start your project</p>
            <h2 className="font-manrope font-bold text-charcoal text-[36px] md:text-[48px] leading-[1.05] tracking-tight mb-4">Get Your Free Design</h2>
            <p className="font-inter text-charcoal/70 text-[16px] md:text-[17px] leading-relaxed mb-3">Tell us a little about your property and what you're planning. We'll review the project and get in touch to discuss the next steps.</p>
            <p className="font-inter text-charcoal/60 text-[14px] mb-10"><span className="font-manrope font-semibold text-charcoal">Free design service</span> — <em>Subject to project feasibility.</em></p>
        </>
    );
}


function Progress({ step }) {
    const labels = ["Property", "Project", "Details"];
    return (
        <div className="mb-10" data-testid="form-progress">
            <p className="font-inter text-charcoal/60 text-[13px] mb-3">Step {step} of 3</p>
            <div className="flex items-center gap-3">
                {labels.map((label, i) => {
                    const n = i + 1;
                    const active = n === step;
                    const done = n < step;
                    return (
                        <React.Fragment key={label}>
                            <div className="flex items-center gap-2.5">
                                <div className={"w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-manrope font-bold transition-colors " + (done ? "bg-charcoal text-offwhite" : active ? "bg-terracotta text-offwhite" : "border border-charcoal/30 text-charcoal/40")}>{done ? <Check size={13} /> : n}</div>
                                <span className={"font-manrope text-[13px] md:text-[14px] " + (active ? "text-charcoal font-semibold" : done ? "text-charcoal/80" : "text-charcoal/40")}>{label}</span>
                            </div>
                            {i < labels.length - 1 && <div className="flex-1 h-px bg-charcoal/15" />}
                        </React.Fragment>
                    );
                })}
            </div>
        </div>
    );
}


function Field({ label, error, children, required }) {
    return (
        <div className="mb-6">
            <label className="block font-manrope font-semibold text-charcoal text-[14px] mb-2">{label}{required && <span className="text-terracotta ml-1">*</span>}</label>
            {children}
            {error && <p className="mt-2 text-terracotta text-[13px] font-inter">{error}</p>}
        </div>
    );
}


const inputCls = "w-full px-4 py-3 bg-offwhite border border-charcoal/20 rounded-md font-inter text-[15px] text-charcoal placeholder:text-charcoal/40 focus:border-terracotta focus:outline-none transition-colors";


export default function EnquiryForm() {
    const attribution = useAttribution();
    const [step, setStep] = useState(1);
    const [data, setData] = useState(INITIAL);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState("");
    const [success, setSuccess] = useState(false);
    const [postcodeWarn, setPostcodeWarn] = useState("");

    const set = (k, v) => { setData((d) => ({ ...d, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); };

    const validateStep = (s) => {
        const e = {};
        if (s === 1) {
            if (!data.postcode.trim()) e.postcode = "Please enter your postcode.";
            if (!data.property_status) e.property_status = "Please choose one.";
            if (data.postcode && !UK_POSTCODE_RE.test(data.postcode.trim())) {
                setPostcodeWarn("That doesn't look like a standard UK postcode — please double-check. You can still continue.");
            } else setPostcodeWarn("");
        } else if (s === 2) {
            if (data.project_types.length === 0) e.project_types = "Pick at least one.";
            if (!data.project_description.trim() || data.project_description.trim().length < 10) e.project_description = "A sentence or two is fine — please give us at least 10 characters.";
        } else if (s === 3) {
            if (!data.construction_budget) e.construction_budget = "Please choose a budget band.";
            if (!data.construction_timing) e.construction_timing = "When are you hoping to start?";
            if (!data.full_name.trim()) e.full_name = "Please tell us your name.";
            if (!data.email.trim() || !/.+@.+\..+/.test(data.email)) e.email = "Please enter a valid email.";
            if (!data.phone.trim() || data.phone.replace(/\D/g, "").length < 6) e.phone = "Please enter a phone number.";
        }
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const next = () => { if (validateStep(step)) setStep((s) => Math.min(3, s + 1)); };
    const back = () => setStep((s) => Math.max(1, s - 1));

    const submit = async () => {
        if (!validateStep(3)) return;
        setSubmitting(true);
        setSubmitError("");
        try {
            const payload = {
                full_name: data.full_name.trim(),
                email: data.email.trim(),
                phone: data.phone.trim(),
                postcode: data.postcode.trim(),
                property_status: data.property_status,
                project_types: data.project_types,
                project_description: data.project_description.trim(),
                construction_budget: data.construction_budget,
                construction_timing: data.construction_timing,
                website: data.website,
                attribution,
            };
            await axios.post(`${API}/enquiries`, payload);
            setSuccess(true);
        } catch (err) {
            const status = err?.response?.status;
            if (status === 400) {
                const detail = err.response?.data?.detail;
                if (detail && Array.isArray(detail.errors)) {
                    const fieldToStep = { postcode: 1, property_status: 1, project_types: 2, project_description: 2, construction_budget: 3, construction_timing: 3, full_name: 3, email: 3, phone: 3 };
                    const newErrs = {};
                    let firstStep = 3;
                    detail.errors.forEach((e2) => { newErrs[e2.field] = e2.message; if (fieldToStep[e2.field] && fieldToStep[e2.field] < firstStep) firstStep = fieldToStep[e2.field]; });
                    setErrors(newErrs);
                    setStep(firstStep);
                    setSubmitError("Please correct the highlighted fields.");
                } else {
                    setSubmitError(typeof detail === "string" ? detail : "Please review your details and try again.");
                }
            } else if (status === 413) {
                setSubmitError("Your description is too long — please shorten it and try again.");
            } else if (status === 429) {
                setSubmitError("Too many requests — please wait a moment and try again.");
            } else {
                setSubmitError("Something went wrong. Please try again or email us at __EMAIL__.");
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (success) {
        return (
            <section id="enquiry-form" className="bg-offwhite py-12 md:py-16" data-testid="section-enquiry-form">
                <div className="max-w-[640px] mx-auto px-6 md:px-10 text-center" data-testid="form-success">
                    <div className="w-14 h-14 rounded-full bg-terracotta/10 mx-auto flex items-center justify-center mb-6"><Check size={28} className="text-terracotta" /></div>
                    <h2 className="font-manrope font-bold text-charcoal text-[30px] md:text-[38px] leading-tight mb-6">Thanks — we've received your project details.</h2>
                    <p className="font-inter text-charcoal/80 text-[16px] md:text-[17px] leading-relaxed mb-4">We'll review the information you've provided and contact you to discuss your renovation.</p>
                    <p className="font-inter text-charcoal/70 text-[15px] md:text-[16px] leading-relaxed">If you have existing plans, estate-agent particulars or photographs, you can send them to us once we make contact.</p>
                </div>
            </section>
        );
    }

    return (
        <section id="enquiry-form" className="bg-offwhite py-12 md:py-16" data-testid="section-enquiry-form">
            <div className="max-w-[1200px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-2 gap-x-12 md:gap-x-20">
                <div><Kicker /></div>
                <div>
                <Progress step={step} />

                {step === 1 && (
                    <div data-testid="form-step-1">
                        <Field label="Property postcode" required error={errors.postcode}>
                            <input type="text" value={data.postcode} onChange={(e) => set("postcode", e.target.value.toUpperCase())} placeholder="e.g. SW1A 1AA" autoComplete="postal-code" className={inputCls} data-testid="input-postcode" />
                            {postcodeWarn && <p className="mt-2 text-charcoal/60 text-[13px] font-inter italic">{postcodeWarn}</p>}
                        </Field>
                        <Field label="Which best describes your situation?" required error={errors.property_status}>
                            <div className="space-y-2">
                                {PROPERTY_STATUS_OPTIONS.map((o) => (
                                    <label key={o.value} className="flex items-start gap-3 p-3.5 border border-charcoal/15 rounded-md cursor-pointer hover:border-terracotta/60 transition-colors">
                                        <input type="radio" name="property_status" value={o.value} checked={data.property_status === o.value} onChange={(e) => set("property_status", e.target.value)} className="mt-1 accent-terracotta" data-testid={`radio-property-${o.value}`} />
                                        <span className="font-inter text-[15px] text-charcoal">{o.label}</span>
                                    </label>
                                ))}
                            </div>
                        </Field>
                        <div className="flex justify-end mt-8">
                            <button type="button" onClick={next} className="font-manrope font-bold text-[15px] px-7 py-3 rounded-md bg-terracotta text-offwhite hover:bg-terracotta-600 transition-colors" data-testid="form-next-1">Continue &rarr;</button>
                        </div>
                    </div>
                )}

                {step === 2 && (
                    <div data-testid="form-step-2">
                        <Field label="What are you considering?" required error={errors.project_types}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                {PROJECT_TYPE_OPTIONS.map((o) => {
                                    const checked = data.project_types.includes(o.value);
                                    return (
                                        <label key={o.value} className={"flex items-start gap-3 p-3.5 border rounded-md cursor-pointer transition-colors " + (checked ? "border-terracotta bg-terracotta/5" : "border-charcoal/15 hover:border-terracotta/60")}>
                                            <input type="checkbox" checked={checked} onChange={(e) => { const list = e.target.checked ? [...data.project_types, o.value] : data.project_types.filter((v) => v !== o.value); set("project_types", list); }} className="mt-1 accent-terracotta" data-testid={`check-project-${o.value}`} />
                                            <span className="font-inter text-[15px] text-charcoal">{o.label}</span>
                                        </label>
                                    );
                                })}
                            </div>
                        </Field>
                        <Field label="Tell us briefly what you're hoping to achieve" required error={errors.project_description}>
                            <textarea rows={5} value={data.project_description} onChange={(e) => set("project_description", e.target.value.slice(0, 5000))} placeholder="Extension, loft conversion, open-plan kitchen, additional bedroom, full refurbishment, etc." className={inputCls + " resize-y"} data-testid="input-description" />
                            <div className="flex justify-end mt-1 font-inter text-[12px] text-charcoal/50">{data.project_description.length} / 5000</div>
                        </Field>
                        <div className="flex justify-between mt-8">
                            <button type="button" onClick={back} className="font-manrope font-semibold text-[14px] text-charcoal/70 hover:text-charcoal" data-testid="form-back-2">&larr; Back</button>
                            <button type="button" onClick={next} className="font-manrope font-bold text-[15px] px-7 py-3 rounded-md bg-terracotta text-offwhite hover:bg-terracotta-600 transition-colors" data-testid="form-next-2">Continue &rarr;</button>
                        </div>
                    </div>
                )}

                {step === 3 && (
                    <div data-testid="form-step-3">
                        <Field label="What is your anticipated budget for the construction works?" required error={errors.construction_budget}>
                            <div className="space-y-2">
                                {BUDGET_OPTIONS.map((o) => (
                                    <label key={o.value} className="flex items-start gap-3 p-3 border border-charcoal/15 rounded-md cursor-pointer hover:border-terracotta/60 transition-colors">
                                        <input type="radio" name="construction_budget" value={o.value} checked={data.construction_budget === o.value} onChange={(e) => set("construction_budget", e.target.value)} className="mt-1 accent-terracotta" data-testid={`radio-budget-${o.value}`} />
                                        <span className="font-inter text-[15px] text-charcoal">{o.label}</span>
                                    </label>
                                ))}
                            </div>
                        </Field>
                        <Field label="When would you ideally like construction to begin?" required error={errors.construction_timing}>
                            <div className="space-y-2">
                                {TIMING_OPTIONS.map((o) => (
                                    <label key={o.value} className="flex items-start gap-3 p-3 border border-charcoal/15 rounded-md cursor-pointer hover:border-terracotta/60 transition-colors">
                                        <input type="radio" name="construction_timing" value={o.value} checked={data.construction_timing === o.value} onChange={(e) => set("construction_timing", e.target.value)} className="mt-1 accent-terracotta" data-testid={`radio-timing-${o.value}`} />
                                        <span className="font-inter text-[15px] text-charcoal">{o.label}</span>
                                    </label>
                                ))}
                            </div>
                        </Field>
                        <Field label="Full name" required error={errors.full_name}>
                            <input type="text" value={data.full_name} onChange={(e) => set("full_name", e.target.value)} autoComplete="name" className={inputCls} data-testid="input-full-name" />
                        </Field>
                        <Field label="Email" required error={errors.email}>
                            <input type="email" value={data.email} onChange={(e) => set("email", e.target.value)} autoComplete="email" className={inputCls} data-testid="input-email" />
                        </Field>
                        <Field label="Telephone number" required error={errors.phone}>
                            <input type="tel" value={data.phone} onChange={(e) => set("phone", e.target.value)} autoComplete="tel" className={inputCls} data-testid="input-phone" />
                        </Field>
                        <div className="lrs-honeypot" aria-hidden="true">
                            <label>Website (leave blank)<input type="text" tabIndex={-1} autoComplete="off" value={data.website} onChange={(e) => set("website", e.target.value)} data-testid="input-honeypot" /></label>
                        </div>
                        <p className="mt-2 font-inter text-[13px] text-charcoal/60">
                            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-terracotta" data-testid="privacy-link">Read our Privacy Policy</a>
                        </p>
                        {submitError && (
                            <p className="mt-4 p-3 rounded-md bg-terracotta/10 border border-terracotta/30 text-terracotta text-[14px] font-inter" data-testid="form-submit-error">
                                {submitError.includes("__EMAIL__") ? (<>{submitError.split("__EMAIL__")[0]}<ObfuscatedEmail user="hello" domain="londonrenovationspecialists.com" className="underline" />{submitError.split("__EMAIL__")[1]}</>) : submitError}
                            </p>
                        )}
                        <div className="flex flex-col-reverse md:flex-row justify-between md:items-center gap-4 mt-8">
                            <button type="button" onClick={back} className="font-manrope font-semibold text-[14px] text-charcoal/70 hover:text-charcoal md:self-center" data-testid="form-back-3">&larr; Back</button>
                            <button type="button" onClick={submit} disabled={submitting} className="font-manrope font-bold text-[15px] md:text-[16px] px-8 py-3.5 rounded-md bg-terracotta text-offwhite hover:bg-terracotta-600 disabled:opacity-70 disabled:cursor-not-allowed transition-colors" data-testid="form-submit">{submitting ? "Sending\u2026" : "Request My Free Design"}</button>
                        </div>
                    </div>
                )}
                </div>
            </div>
        </section>
    );
}
